import { Button, InputField, Select, TextButton, Toggle } from "kubra-ux-forge";
import React, { ChangeEvent, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuOption } from "kubra-ux-forge/dist/elements/MenuList/hooks/useMenuList";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ACTION } from "actions/paymentIntentActions";
import { useTranslation, Trans } from "react-i18next";
import {
  BANK_ACCCOUNT_MODAL,
  COMMON_NAMESPACE,
  SELECT_PAYMENT_METHOD_MODAL_NAMESPACE,
} from "constants/i18n-namespaces";
import FundingOption from "constants/FundingOption";
import { AnalyticsEventType, useTracking } from "kubra-ui-lib-mfe";
import type { AnalyticsEvent } from "kubra-ui-lib-mfe";
import { PLATFORM_NAMESPACE } from "kubra-labels";
import { MethodComponentsOptions } from "../../payable-dashboard/payable-dashboard";
import { MAXIMUM_WALLET_ITEMS } from "../select-payment-method-modal";
import useWallet from "hooks/useWallet";
import { WarningInformationBox } from "components/ezpay/common/warning-information-box/warning-information-box";
import { getPayableSession } from "selectors/payableSession";
import { getBiller } from "selectors";
import { TermsLink, PrivacyLink } from "components/ezpay/common/terms-privacy-links/terms-privacy-links";

export interface IPayableBankAccount {
  bankAccountNumber: string;
  routingNumber: string;
  accountOwnerName: string;
  accountType: string;
  saveToWallet: boolean;
}
export const PayableBankAccountInitialState: IPayableBankAccount = {
  bankAccountNumber: "",
  routingNumber: "",
  accountOwnerName: "",
  accountType: "",
  saveToWallet: false,
};

export interface IPayableBankAccountModalProps {
  flowCallback: (method: MethodComponentsOptions) => void;
  closeModalCallBack: () => void;
  showManagePaymentMethodsModal: () => void;
  showAccountHelpAccordion?: boolean;
}

export const PayableBankAccountModal = (
  props: IPayableBankAccountModalProps
) => {
  const payableSession = useSelector(getPayableSession);
  const { t } = useTranslation(BANK_ACCCOUNT_MODAL);
  const { flowCallback, closeModalCallBack } = props;
  const dispatch = useDispatch();
  const { trackEvent } = useTracking<Partial<AnalyticsEvent>>();

  const { getWalletItems } = useWallet(true);
  const walletItems = getWalletItems();

  const saveToWalletAllowed = walletItems.length < MAXIMUM_WALLET_ITEMS;

  const bankAccountTypes = useMemo(
    () => [
      {
        name: t("checking"),
        value: "Checking",
      },
      {
        name: t("savings"),
        value: "Savings",
      },
      {
        name: t("business-checking"),
        value: "BusinessChecking",
      },
      {
        name: t("business-savings"),
        value: "BusinessSavings",
      },
    ],
    [t]
  );

  const handleButtonCancel = () => {
    flowCallback("SelectPaymentMethodModal");
  };

  const handleToggleChange = () => {
    setFieldValue("saveToWallet", !values.saveToWallet);
  };

  const handleRoutingNumberChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const regex = /^[0-9\b]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("routingNumber", event.currentTarget.value);
    }
  };

  const handleBankAccNumberChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const regex = /^[0-9\b]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("bankAccountNumber", event.currentTarget.value);
    }
  };

  const handleAccOwnerNameChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const regex = /^[a-zA-Z\s]+$/;
    if (
      event.currentTarget.value === "" ||
      regex.test(event.currentTarget.value)
    ) {
      await setFieldValue("accountOwnerName", event.currentTarget.value);
    }
  };

  const handleManagePaymentMethods = () => {
    props.closeModalCallBack();
    props.showManagePaymentMethodsModal();
  };

  const formik = useFormik({
    initialValues: { ...PayableBankAccountInitialState },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object({
      accountOwnerName: Yup.string()
        .required(t("account-owner-name-required"))
        .matches(/^[a-zA-Z\s]+$/, t("account-owner-name-required")),
      accountType: Yup.string()
        .required(t("account-type-required"))
        .oneOf(
          bankAccountTypes.map((accountType) => accountType.value),
          t("account-type-required")
        ),
      routingNumber: Yup.string()
        .required(t("routing-number-required"))
        .matches(/^[0-9\b]+$/, t("routing-number-required"))
        .max(9, t("routing-number-invalid"))
        .min(9, t("routing-number-invalid"))
        .test("routingNumber", t("routing-number-required"), function (value) {
          return +value > 0;
        }),
      bankAccountNumber: Yup.string()
        .required(t("bank-account-required"))
        .matches(/^[0-9\b]+$/, t("bank-account-required"))
        .max(17, t("bank-account-invalid"))
        .test(
          "bankAccountNumber",
          t("bank-account-required"),
          function (value) {
            return +value > 0;
          }
        ),
      saveToWallet: Yup.bool(),
    }),
    onSubmit: (values: IPayableBankAccount, actions: any) => {
      actions.setSubmitting(true);
      dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
      dispatch({ type: ACTION.ADD_WALLET, payload: null });
      dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: values });
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.BankAccount,
      });
      actions.resetForm();
      setSubmitting(false);
      trackEvent({
        type: AnalyticsEventType.customEvent,
        event: "bank_created",
      });
      closeModalCallBack();
    },
  });

  const {
    values,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
    setSubmitting,
    handleBlur,
    handleSubmit,
  } = formik;

  const accountOwnerNameError =
    touched.accountOwnerName ? errors.accountOwnerName : undefined;
  const accountTypeError = touched.accountType ? errors.accountType : undefined;
  const routingNumberError =
    touched.routingNumber ? errors.routingNumber : undefined;
  const bankAccountNumberError =
    touched.bankAccountNumber ? errors.bankAccountNumber : undefined;

  const accountOwnerNameErrorId = "payable-bank-account-owner-name-error";
  const accountTypeErrorId = "payable-bank-account-type-error";
  const routingNumberErrorId = "payable-bank-routing-number-error";
  const bankAccountNumberErrorId = "payable-bank-account-number-error";

  useEffect(() => {
    const errorIdMap: Array<[string, string, string | undefined]> = [
      [
        "input-account-owner-name-error-message",
        accountOwnerNameErrorId,
        accountOwnerNameError,
      ],
      ["select-account-type-error-message", accountTypeErrorId, accountTypeError],
      [
        "input-routing-number-error-message",
        routingNumberErrorId,
        routingNumberError,
      ],
      [
        "input-bank-account-number-error-message",
        bankAccountNumberErrorId,
        bankAccountNumberError,
      ],
    ];

    errorIdMap.forEach(([dataCy, id, errorText]) => {
      if (!errorText) {
        return;
      }

      const errorElement = document.querySelector(`[data-cy='${dataCy}']`);
      if (errorElement) {
        errorElement.setAttribute("id", id);
      }
    });
  }, [
    accountOwnerNameError,
    accountTypeError,
    routingNumberError,
    bankAccountNumberError,
  ]);

  const biller = useSelector(getBiller);

  const saveBankAccountContainerClass = !saveToWalletAllowed
    ? "save-bank-account-container-disabled"
    : values.saveToWallet
      ? "save-bank-account-container-enabled"
      : "save-bank-account-container"

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <div className="bank-account-container">
        <div className="bank-account-inputs-container">
          <div className="bank-account-name-textbox-container">
            <InputField
              name="accountOwnerName"
              id="accountOwnerName"
              aria-required="true"
              errorText={accountOwnerNameError}
              helperText=""
              label={`${t("account-owner-name-label")}`}
              value={values.accountOwnerName}
              onChange={handleAccOwnerNameChange}
              onBlur={handleBlur}
              hasError={
                touched.accountOwnerName && errors.accountOwnerName != undefined
              }
              aria-describedby={
                accountOwnerNameError ? accountOwnerNameErrorId : undefined
              }
              dataCy="input-account-owner-name"
              aria-invalid={touched.accountOwnerName && !!errors.accountOwnerName}
            />
          </div>
        </div>
        <div className="bank-account-inputs-container">
          <div className="bank-account-name-textbox-container">
            <Select
              name="accountType"
              aria-required="true"
              errorText={accountTypeError}
              helperText=""
              label={`${t("account-type-label")}`}
              onClick={(event: any) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onChange={(option: MenuOption[]) => {
                setFieldValue("accountType", option[0].id);
              }}
              placeholder={`${t("select", { ns: COMMON_NAMESPACE })}`}
              options={bankAccountTypes.map(
                (accountType) =>
                ({
                  id: accountType.value,
                  name: accountType.name,
                } as MenuOption)
              )}
              hasError={touched.accountType && errors.accountType != undefined}
              aria-describedby={accountTypeError ? accountTypeErrorId : undefined}
              dataCy="select-account-type"
              aria-invalid={touched.accountType && !!errors.accountType}
            />
          </div>
        </div>
        <div className="bank-account-routing-container">
          <div className="bank-account-routing-textbox">
            <InputField
              name="routingNumber"
              aria-required="true"
              errorText={routingNumberError}
              hasShowHideButton
              helperText=""
              label={`${t("routing-number-label")}`}
              placeholder=""
              maxLength="9"
              value={values.routingNumber}
              onChange={handleRoutingNumberChange}
              onBlur={handleBlur}
              hasError={
                touched.routingNumber && errors.routingNumber != undefined
              }
              aria-describedby={
                routingNumberError ? routingNumberErrorId : undefined
              }
              dataCy="input-routing-number"
              aria-invalid={touched.routingNumber && !!errors.routingNumber}
            />
          </div>
          <div className="bank-account-routing-textbox">
            <InputField
              name="bankAccountNumber"
              aria-required="true"
              errorText={bankAccountNumberError}
              hasShowHideButton
              helperText=""
              label={`${t("bank-account-label")}`}
              placeholder=""
              maxLength="17"
              value={values.bankAccountNumber}
              onChange={handleBankAccNumberChange}
              onBlur={handleBlur}
              hasError={
                touched.bankAccountNumber &&
                errors.bankAccountNumber != undefined
              }
              aria-describedby={
                bankAccountNumberError ? bankAccountNumberErrorId : undefined
              }
              dataCy="input-bank-account-number"
              aria-invalid={touched.bankAccountNumber && !!errors.bankAccountNumber}
            />
          </div>
        </div>

        {payableSession?.payable?.category?.toLowerCase() === "account" ? (
          <div className="save-bank-account-inputs-container">
            <div
              className={
                saveBankAccountContainerClass
              }
            >
              <div className="save-bank-account-title-toggle-container">
                <span className="save-bank-account-title">
                  {t("save-bank-account-toggle-label")}
                </span>
                <Toggle
                  label=""
                  onChange={handleToggleChange}
                  isCheckedDefault={values.saveToWallet}
                  dataCy="toggle-save-to-wallet"
                  disabled={!saveToWalletAllowed}
                />
              </div>
              {saveToWalletAllowed && (
                <>
                  <div
                    className={
                      values.saveToWallet
                        ? "save-bank-account-line-separator-enabled"
                        : "save-bank-account-line-separator"
                    }
                  />

                  <div className="save-bank-account-text">
                    <Trans
                      i18nKey="save-bank-account-credential"
                      ns={BANK_ACCCOUNT_MODAL}
                      values={{
                        billerName: biller.billerName,
                        lastFourDigits:
                          values.bankAccountNumber && !errors.bankAccountNumber
                            ? values.bankAccountNumber.substring(
                              values.bankAccountNumber.length - 4
                            )
                            : "_____",
                      }}
                      components={[<TermsLink key="terms" />, <PrivacyLink key="privacy" />]}
                    />
                  </div>
                </>
              )}
            </div>
            {!saveToWalletAllowed && (
              <WarningInformationBox>
                <Trans
                  i18nKey="max-payment-methods-message"
                  ns={SELECT_PAYMENT_METHOD_MODAL_NAMESPACE}
                  components={{
                    removeAction: (
                      <TextButton
                        onClick={handleManagePaymentMethods}
                        hoverStyle="underline"
                      >
                        {t("remove-payment-method")}
                      </TextButton>
                    ),
                  }}
                />
              </WarningInformationBox>
            )}
          </div>
        ) : null}
        <div className="buttons-row">
          <div className="button-item">
            <TextButton
              type="button"
              onClick={handleButtonCancel}
              dataCy="button-cancel"
            >
              {t("cancelLabel", { ns: PLATFORM_NAMESPACE })}
            </TextButton>
          </div>
          <div className="button-item">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => null}
              dataCy="button-done"
            >
              {t("done", { ns: COMMON_NAMESPACE })}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};
